/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Attach a call to a record, at the moment the agent knows who it was about.
 *
 * Wrap-up is the only point where this reliably happens. Before the call the
 * agent does not know; after it they have moved on. So both halves live here:
 * find an existing record, or create one from what the call already knows.
 *
 * The phone number is never retyped. It comes off the conversation, which is
 * the entire reason creating from here beats opening the module in another
 * tab: a number typed from memory after a call is the number that fails to
 * match on the next one.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Business,
  ConfirmationNumber,
  LocalOffer,
  Person,
  PersonSearch,
  Search,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";

const cc = colors.callCenter;

const TYPES = [
  { value: "contact", label: "Contact", icon: <Person fontSize="small" />, table: "contacts" },
  { value: "lead", label: "Lead", icon: <PersonSearch fontSize="small" />, table: "leads" },
  { value: "account", label: "Account", icon: <Business fontSize="small" />, table: "accounts" },
  { value: "deal", label: "Deal", icon: <LocalOffer fontSize="small" />, table: "deals" },
  { value: "case", label: "Case", icon: <ConfirmationNumber fontSize="small" />, table: "cases" },
];

const typeMeta = (value) => TYPES.find((t) => t.value === value) || TYPES[0];

export default function LinkRecordPanel({ conversation, onLinked, disabled }) {
  const { axiosInstance } = useAuth();

  const [mode, setMode] = useState("find");
  const [recordType, setRecordType] = useState("contact");
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");

  const linked = useMemo(() => {
    if (!conversation) return null;
    const t = conversation.primary_parent_table;
    if (!t) return null;
    return {
      table: t,
      label: conversation.caller_name || conversation.contact_name ||
             conversation.account_name || "Linked",
    };
  }, [conversation]);

  // Debounced so a four-letter name is one query, not four.
  useEffect(() => {
    if (mode !== "find" || term.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const id = setTimeout(() => {
      axiosInstance
        .get("/call-console/search", { params: { q: term.trim() } })
        .then((r) => {
          if (!cancelled) setResults(Array.isArray(r.data) ? r.data : []);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [term, mode, axiosInstance]);

  const link = useCallback(
    async (match) => {
      if (!conversation) return;
      setSaving(true);
      setError("");
      try {
        const table = typeMeta(match.type).table;
        const res = await axiosInstance.post(
          `/call-console/conversations/${conversation.id}/link`,
          {
            parent_table: table,
            parent_id: match.id,
            account_id: match.type === "account" ? match.id : match.account_id || null,
            contact_id: match.type === "contact" ? match.id : null,
            lead_id: match.type === "lead" ? match.id : null,
            deal_id: match.type === "deal" ? match.id : null,
            case_id: match.type === "case" ? match.id : null,
          }
        );
        setTerm("");
        setResults([]);
        onLinked(res.data, `Call linked to ${match.name}.`);
      } catch (err) {
        setError(getApiErrorMessage(err, "Could not link the call."));
      } finally {
        setSaving(false);
      }
    },
    [axiosInstance, conversation, onLinked]
  );

  const create = useCallback(async () => {
    if (!conversation) return;
    setSaving(true);
    setError("");
    try {
      const res = await axiosInstance.post(
        `/call-console/conversations/${conversation.id}/create-record`,
        {
          record_type: recordType,
          name: name.trim(),
          company_name: company.trim() || null,
          email: email.trim() || null,
          subject: subject.trim() || null,
        }
      );
      setName("");
      setCompany("");
      setEmail("");
      setSubject("");
      onLinked(res.data, `${typeMeta(recordType).label} created and linked.`);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not create the record."));
    } finally {
      setSaving(false);
    }
  }, [axiosInstance, conversation, recordType, name, company, email, subject, onLinked]);

  const isCase = recordType === "case";
  // A deal is raised against whoever the call is already attached to, so the
  // only thing to collect is what to call it.
  const isDeal = recordType === "deal";
  const canCreate = isCase ? subject.trim().length > 0 : name.trim().length > 0;

  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      {linked ? (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography variant="caption" color="text">
            Linked to
          </MDTypography>
          <Chip size="small" label={linked.label} color="info" variant="outlined" />
        </MDBox>
      ) : (
        <MDTypography variant="caption" color="text">
          {disabled
            ? "No call is bound to this console yet, so there is nothing to attach."
            : "This call is not attached to any record yet."}
        </MDTypography>
      )}

      <MDBox display="flex" gap={1}>
        <MDButton
          size="small"
          variant={mode === "find" ? "gradient" : "outlined"}
          color="info"
          disabled={disabled}
          onClick={() => setMode("find")}
        >
          Find existing
        </MDButton>
        <MDButton
          size="small"
          variant={mode === "create" ? "gradient" : "outlined"}
          color="info"
          disabled={disabled}
          onClick={() => setMode("create")}
        >
          Create new
        </MDButton>
      </MDBox>

      {error ? (
        <MDTypography variant="caption" sx={{ color: cc.danger }}>
          {error}
        </MDTypography>
      ) : null}

      {mode === "find" ? (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, company or email"
            value={term}
            disabled={disabled || saving}
            onChange={(e) => setTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ fontSize: 18, color: cc.muted, mr: 1 }} />,
            }}
          />
          {searching ? (
            <MDBox display="flex" justifyContent="center" py={1}>
              <CircularProgress size={18} />
            </MDBox>
          ) : null}
          {!searching && term.trim().length >= 2 && results.length === 0 ? (
            <MDTypography variant="caption" color="text">
              Nothing matched. Create a new record instead.
            </MDTypography>
          ) : null}
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            {results.map((m) => (
              <MDBox
                key={`${m.type}-${m.id}`}
                onClick={() => !saving && link(m)}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  p: 1,
                  borderRadius: "8px",
                  cursor: saving ? "default" : "pointer",
                  border: `1px solid ${cc.cardBorder}`,
                  "&:hover": { bgcolor: cc.surface },
                }}
              >
                <MDBox sx={{ color: cc.muted, display: "flex" }}>
                  {typeMeta(m.type).icon}
                </MDBox>
                <MDBox flexGrow={1} minWidth={0}>
                  <MDTypography variant="button" fontWeight="medium" color="dark" display="block">
                    {m.name}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {[typeMeta(m.type).label, m.subtitle].filter(Boolean).join(" · ")}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        </>
      ) : (
        <>
          <TextField
            select
            size="small"
            fullWidth
            label="Type"
            value={recordType}
            disabled={disabled || saving}
            onChange={(e) => setRecordType(e.target.value)}
          >
            {TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          {isCase ? (
            <TextField
              size="small"
              fullWidth
              label="Subject"
              value={subject}
              disabled={disabled || saving}
              onChange={(e) => setSubject(e.target.value)}
              helperText={
                linked
                  ? `Will be raised against ${linked.label}.`
                  : "Not attached to a customer yet."
              }
            />
          ) : (
            <>
              <TextField
                size="small"
                fullWidth
                label={
                  recordType === "account"
                    ? "Account name"
                    : isDeal
                    ? "Deal name"
                    : "Full name"
                }
                value={name}
                disabled={disabled || saving}
                onChange={(e) => setName(e.target.value)}
                helperText={
                  isDeal
                    ? linked
                      ? `Will be opened against ${linked.label}.`
                      : "Not attached to a customer yet."
                    : undefined
                }
              />
              {recordType === "lead" ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Company"
                  value={company}
                  disabled={disabled || saving}
                  onChange={(e) => setCompany(e.target.value)}
                />
              ) : null}
              {recordType !== "account" && !isDeal ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Email"
                  value={email}
                  disabled={disabled || saving}
                  onChange={(e) => setEmail(e.target.value)}
                />
              ) : null}
            </>
          )}

          <Tooltip title="The caller's number is taken from the call, not retyped.">
            <span>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                disabled={disabled || saving || !canCreate}
                onClick={create}
              >
                {saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  `Create ${typeMeta(recordType).label.toLowerCase()} & link`
                )}
              </MDButton>
            </span>
          </Tooltip>
        </>
      )}
    </MDBox>
  );
}
